import { FC } from "react";
import Slider from "rc-slider";
import { convertNumbThousand } from "@1-now/booking-core";
import { allSelected, toggleOption } from "utils/typeSelection";

export interface FilterSidebarProps {
  types: string[];
  selectedTypes: string[];
  setSelectedTypes: (types: string[]) => void;
  rangePrices: number[];
  setRangePrices: (range: number[]) => void;
  maxPrice: number;
  /** How many cars each class holds, to show beside it. */
  counts?: Record<string, number>;
}

/**
 * The Vehicles filters as a left sidebar (1NOW-11175): the car type as a checklist and the
 * daily price as a range. It is controlled: the page owns the state (useFleetFilters), so the
 * chips above the grid, "Clear all" and a `?class=` link all move the same boxes. The same
 * two filters the old pop-ups had, applied together by the same hook (1NOW-11129).
 */
const FilterSidebar: FC<FilterSidebarProps> = ({
  types,
  selectedTypes,
  setSelectedTypes,
  rangePrices,
  setRangePrices,
  maxPrice,
  counts,
}) => {
  // "Select All" (1NOW-11127) is not state of its own: ticked when every class is picked or none
  // is (no pick filters nothing, so it already means all of them), unticked as soon as only some
  // are. Ticking it picks them all; unticking it from "all picked" clears them.
  const everyType = allSelected(types, selectedTypes);

  return (
    <div className="db-card db-filters">
      {types.length > 0 && (
        <fieldset className="db-filters__group">
          <legend className="db-filters__h">Vehicle type</legend>
          <ul className="db-filters__list">
            <li>
              <label className="db-filters__opt">
                <input
                  type="checkbox"
                  className="db-check"
                  checked={everyType}
                  // 1NOW-11193: Select All drops the type filter, so it lists every car, the
                  // untyped ones too, exactly as Clear all does. Ticking each type showed only
                  // the typed cars (3 of 80 on QA) under the same ticked box.
                  onChange={() => setSelectedTypes([])}
                />
                <span className="min-w-0 flex-1 truncate font-semibold">Select All</span>
              </label>
            </li>
            {types.map((type) => (
              <li key={type}>
                <label className="db-filters__opt">
                  <input
                    type="checkbox"
                    className="db-check"
                    checked={selectedTypes.includes(type)}
                    onChange={(e) => setSelectedTypes(toggleOption(selectedTypes, type, e.target.checked))}
                  />
                  <span className="min-w-0 flex-1 truncate">{type}</span>
                  {counts?.[type] !== undefined && <span className="db-filters__n">{counts[type]}</span>}
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
      )}

      {maxPrice > 0 && (
        <fieldset className="db-filters__group">
          <legend className="db-filters__h">Daily price</legend>
          <div className="px-1.5 pb-1 pt-3">
            <Slider
              range
              min={0}
              max={maxPrice}
              step={1}
              allowCross={false}
              value={[rangePrices[0], rangePrices[1]]}
              onChange={(value) => setRangePrices(value as number[])}
              ariaLabelForHandle={["Lowest daily price", "Highest daily price"]}
            />
          </div>
          <div className="mt-3 flex items-center justify-between text-[13px] font-extrabold text-text-color">
            <span>${convertNumbThousand(rangePrices[0])}</span>
            <span>${convertNumbThousand(rangePrices[1])}</span>
          </div>
        </fieldset>
      )}
    </div>
  );
};

export default FilterSidebar;
